import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	redirect,
	Scripts,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import ComponentInitializer from "#/components/initializer/ComponentInitializer.tsx";
import { THEME_INIT_SCRIPT } from "#/lib/theme";
import { canReloadNow, isVersionStale } from "#/lib/version-check";
import { AttributionSync } from "../components/analytics/attribution-sync";
import { PageViewSync } from "../components/analytics/pageview-sync";
import { LocaleSync } from "../components/locale/locale-sync";
import { NotFound } from "../components/not-found";
import { SeoSync } from "../components/seo/seo-sync";
import { ThemeSync } from "../components/theme/theme-sync.tsx";
import appCss from "../styles.css?url";

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	// 배포 버전 스큐 게이트 — 새 배포가 감지된 탭은 다음 내비게이션을 전체 페이지
	// 로드로 전환해 옛 번들에서 벗어난다(스테일 청크 요청 자체가 사라짐).
	beforeLoad: ({ location, preload }) => {
		if (!preload && isVersionStale() && canReloadNow()) {
			throw redirect({ href: location.href, reloadDocument: true });
		}
	},
	// 런타임에 아무도 다시 쓰지 않는 불변 기본값만 head()에 둔다. SeoSync/부트
	// IIFE가 라우트·로케일별로 재작성하는 태그(title/description/OG/canonical…)는
	// RootDocument의 raw JSX — head 매니저가 내비게이션마다 EN 기본값으로
	// 되돌리지 않게 하기 위한 분리다(Phase 3에서 head()로 통합 예정).
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{ name: "viewport", content: "width=device-width, initial-scale=1" },
			{
				name: "google-site-verification",
				content: "qT3Msuiqrrij5p8UnNKcMsDLnFs54KYAzVNTKMVlGp4",
			},
			{ name: "theme-color", content: "#000000" },
		],
		links: [
			{ rel: "icon", href: "/favicon.ico", sizes: "any" },
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32x32.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "/favicon-16x16.png",
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{ rel: "manifest", href: "/manifest.json" },
			{
				rel: "preconnect",
				href: "https://app.hyperliquid.xyz",
				crossOrigin: "",
			},
			{ rel: "dns-prefetch", href: "https://api.hyperliquid.xyz" },
			{ rel: "stylesheet", href: appCss },
		],
	}),
	shellComponent: RootDocument,
	component: RootLayout,
	notFoundComponent: NotFound,
});

const DEFAULT_TITLE = "Tenbagger | Global Perpetual Trading";
const DEFAULT_DESCRIPTION =
	"Trade crypto and global perpetual futures on Tenbagger. Track markets, manage positions, and learn the platform in one interface.";

// GA4 부트스트랩 — 페이지뷰는 PageViewSync가 쏘므로 send_page_view:false.
const GTAG_INIT_SCRIPT = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-QH0E90GYD7',{send_page_view:false});`;

// Organization + WebSite @graph — 라우트별 JSON-LD(#organization 참조)의 앵커.
const ORG_JSON_LD = JSON.stringify({
	"@context": "https://schema.org",
	"@graph": [
		{
			"@type": "Organization",
			"@id": "https://tenbagger.xyz/#organization",
			name: "Tenbagger",
			alternateName: "텐배거",
			url: "https://tenbagger.xyz",
			logo: {
				"@type": "ImageObject",
				url: "https://tenbagger.xyz/images/tenbagger_preview.jpg",
				width: 1200,
				height: 630,
			},
			sameAs: ["https://x.com/tenbagger_xyz"],
		},
		{
			"@type": "WebSite",
			"@id": "https://tenbagger.xyz/#website",
			url: "https://tenbagger.xyz",
			name: "Tenbagger",
			alternateName: "텐배거",
			description: DEFAULT_DESCRIPTION,
			publisher: { "@id": "https://tenbagger.xyz/#organization" },
			inLanguage: ["en", "ko", "zh"],
		},
	],
});

// 프리-하이드레이션 로케일 + SEO 사전 — 구 index.html:81 verbatim. 크롤러가 JS를
// 돌리기 전의 첫 페인트에서 title/description을 로케일·경로별로 맞춘다.
// Phase 3(SSR head())에서 통째로 삭제 예정.
const LOCALE_SEO_INIT_SCRIPT = `(function(){try{var supported=['en','ko','zh'];var rawPath=window.location.pathname||'/';var m=rawPath.match(/^\\/(ko|zh)(?:\\/|$)/);var locale=m?m[1]:(function(){var stored=window.localStorage.getItem('locale');if(supported.indexOf(stored)!==-1)return stored;var lang=navigator.language.toLowerCase();if(lang.indexOf('ko')===0)return'ko';if(lang.indexOf('zh')===0)return'zh';return'en';})();document.documentElement.lang=locale;var pathname=m?(rawPath.replace(/^\\/(ko|zh)/,'')||'/'):rawPath;var dictionaries={en:{homeTitle:'Tenbagger | Global Perpetual Trading',homeDesc:'Trade crypto and global perpetual futures on Tenbagger. Track markets, manage positions, and learn the platform in one interface.',learnTitle:'Learn | Tenbagger',learnDesc:'Learn how Tenbagger works, from wallet funding and security keys to trading basics and platform FAQs.',portfolioTitle:'Portfolio | Tenbagger',portfolioDesc:'View wallet balances, positions, open orders, recent trades, and account activity in your Tenbagger portfolio.',tradeTitleSuffix:' | Tenbagger',tradeDescPrefix:'Trade ',tradeDescSuffix:' on Tenbagger. Track charts, order flow, positions, and perpetual market activity from a single workspace.'},ko:{homeTitle:'텐배거 | 글로벌 무기한 선물 거래',homeDesc:'Tenbagger에서 암호화폐와 글로벌 무기한 선물을 거래하세요. 하나의 인터페이스에서 마켓 추적, 포지션 관리, 플랫폼 학습까지 처리할 수 있습니다.',learnTitle:'이용 가이드 | 텐배거',learnDesc:'지갑 입금, 보안 키, 거래 기초, 플랫폼 FAQ까지 Tenbagger 사용법을 빠르게 익혀보세요.',portfolioTitle:'포트폴리오 | 텐배거',portfolioDesc:'Tenbagger 포트폴리오에서 지갑 잔고, 포지션, 미체결 주문, 최근 거래, 계정 활동을 확인하세요.',tradeTitleSuffix:' | 텐배거',tradeDescPrefix:'Tenbagger에서 ',tradeDescSuffix:'를 거래하세요. 차트, 오더플로우, 포지션, 무기한 선물 시장 흐름을 한 화면에서 확인할 수 있습니다.'},zh:{homeTitle:'Tenbagger | 全球永续合约交易',homeDesc:'在 Tenbagger 交易加密货币和全球永续合约，在一个界面中完成行情追踪、仓位管理和平台学习。',learnTitle:'学习中心 | Tenbagger',learnDesc:'快速了解 Tenbagger 的使用方式，包括钱包入金、安全密钥、交易基础和平台常见问题。',portfolioTitle:'投资组合 | Tenbagger',portfolioDesc:'在 Tenbagger 投资组合页面查看钱包余额、持仓、挂单、最近成交和账户活动。',tradeTitleSuffix:' | Tenbagger',tradeDescPrefix:'在 Tenbagger 交易 ',tradeDescSuffix:'。在同一个界面中查看图表、订单流、持仓和永续市场动态。'}};var copy=dictionaries[locale]||dictionaries.en;var title=copy.homeTitle;var description=copy.homeDesc;if(pathname==='/learn'){title=copy.learnTitle;description=copy.learnDesc;}else if(pathname==='/portfolio'){title=copy.portfolioTitle;description=copy.portfolioDesc;}else if(pathname.indexOf('/trade/')===0){var rawSymbol=decodeURIComponent(pathname.slice('/trade/'.length))||'BTC';title=rawSymbol.toUpperCase()+'-PERP'+copy.tradeTitleSuffix;description=copy.tradeDescPrefix+rawSymbol.toUpperCase()+'-PERP'+copy.tradeDescSuffix;}document.title=title;var descriptionMeta=document.querySelector('meta[name="description"]');if(descriptionMeta)descriptionMeta.setAttribute('content',description);var ogTitleMeta=document.querySelector('meta[property="og:title"]');if(ogTitleMeta)ogTitleMeta.setAttribute('content',title);var ogDescriptionMeta=document.querySelector('meta[property="og:description"]');if(ogDescriptionMeta)ogDescriptionMeta.setAttribute('content',description);var twitterTitleMeta=document.querySelector('meta[name="twitter:title"]');if(twitterTitleMeta)twitterTitleMeta.setAttribute('content',title);var twitterDescriptionMeta=document.querySelector('meta[name="twitter:description"]');if(twitterDescriptionMeta)twitterDescriptionMeta.setAttribute('content',description);}catch(e){}})();`;

function RootDocument({ children }: { children: ReactNode }) {
	return (
		// suppressHydrationWarning: 테마/로케일 IIFE가 하이드레이션 전에 <html>의
		// class·lang을 바꾼다(의도된 mismatch).
		<html lang="en" suppressHydrationWarning>
			<head>
				<HeadContent />
				{/* 여기부터는 SeoSync/IIFE가 런타임에 재작성하는 태그 — head() 밖 raw JSX */}
				<title>{DEFAULT_TITLE}</title>
				<link rel="canonical" href="https://tenbagger.xyz/" />
				<link rel="alternate" hrefLang="en" href="https://tenbagger.xyz/" />
				<meta name="description" content={DEFAULT_DESCRIPTION} />
				<meta name="robots" content="index, follow" />
				<meta property="og:site_name" content="Tenbagger" />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={DEFAULT_TITLE} />
				<meta property="og:description" content={DEFAULT_DESCRIPTION} />
				<meta
					property="og:image"
					content="https://tenbagger.xyz/images/tenbagger_preview.jpg"
				/>
				<meta property="og:image:type" content="image/jpeg" />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
				<meta
					property="og:image:alt"
					content="Tenbagger — Global Perpetual Trading"
				/>
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={DEFAULT_TITLE} />
				<meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
				<meta
					name="twitter:image"
					content="https://tenbagger.xyz/images/tenbagger_preview.jpg"
				/>
				<script
					async
					src="https://www.googletagmanager.com/gtag/js?id=G-QH0E90GYD7"
				/>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 부트 스크립트 — 구 index.html 인라인 이전 */}
				<script dangerouslySetInnerHTML={{ __html: GTAG_INIT_SCRIPT }} />
				<script
					type="application/ld+json"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 JSON-LD
					dangerouslySetInnerHTML={{ __html: ORG_JSON_LD }}
				/>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 부트 스크립트 */}
				<script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 부트 스크립트 */}
				<script dangerouslySetInnerHTML={{ __html: LOCALE_SEO_INIT_SCRIPT }} />
			</head>
			<body className="font-sans antialiased wrap-anywhere selection:bg-[color-mix(in_srgb,var(--color-theme-primary-normal)_24%,transparent)]">
				{/* #root 유지: scripts/prerender.ts의 childElementCount 대기 + 기존 DOM 모양 호환 */}
				<div id="root">{children}</div>
				<Scripts />
			</body>
		</html>
	);
}

function RootLayout() {
	return (
		<>
			<ThemeSync />
			<LocaleSync />
			<SeoSync />
			<AttributionSync />
			<PageViewSync />
			<Outlet />
			<ComponentInitializer />
		</>
	);
}
